import Text from "@/components/ui/text";
import React from "react";
import { cn } from "@/utils/utils";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  TooltipProps
} from "recharts";
import {
  NameType,
  ValueType
} from "recharts/types/component/DefaultTooltipContent";

interface Props {
  value: number;
  total: number;
  centerLabel?: string;
  centerValue?: string;
  tooltipLabel?: string;
  valueColor?: string;
  trackColor?: string;
  innerRadius?: number;
  outerRadius?: number;
  compact?: boolean;
}

const FILLED_KEY = "filled";

// Ratio of the ring's inner radius to its outer radius (controls thickness).
// Shared by the compact (numeric px) and modal (percentage) gauges so the
// two can't drift apart.
const RING_INNER_RATIO = 65 / 75;

const GaugeTooltip = ({
  active,
  payload,
  tooltipLabel,
  display
}: TooltipProps<ValueType, NameType> & {
  tooltipLabel: string;
  display: string;
}) => {
  if (!active || !payload?.length) return null;
  const slice = payload[0].payload as { name: string };
  if (slice.name !== FILLED_KEY) return null;
  return (
    <div className="bg-surface-elevation3 border border-border-border1 rounded-lg p-2 shadow-lg">
      <Text variant="smBody" className="text-primary">
        {tooltipLabel}: {display}
      </Text>
    </div>
  );
};

const DreamerGaugeChart: React.FC<Props> = ({
  value,
  total,
  centerLabel,
  centerValue,
  tooltipLabel,
  valueColor = "var(--charts-positive)",
  trackColor = "var(--green-800)",
  innerRadius,
  outerRadius,
  compact = false
}) => {
  if (total <= 0) return null;

  const filled = Math.max(0, Math.min(value, total));
  const remainder = Math.max(0, total - filled);

  const data = [
    { name: FILLED_KEY, value: filled, color: valueColor },
    { name: "remainder", value: remainder, color: trackColor }
  ];
  const showTooltip = !!tooltipLabel && !!centerValue;

  const chartSize = compact ? 180 : 240;
  const compactOuter = chartSize / 2;
  // The preview (compact) gauge sits in a fixed-height slot, so it keeps a
  // fixed square. The modal gauge must scale with its container — otherwise it
  // stays 240px and gets clipped when the modal shrinks on smaller screens.
  // Percentage radii let the ring itself scale with the ResponsiveContainer.
  const ringOuter = outerRadius ?? (compact ? compactOuter : "100%");
  // Derive the fallback inner radius from the resolved outer radius so a
  // custom numeric outerRadius can't leave the ring inverted (inner > outer).
  const ringInner =
    innerRadius ??
    (typeof ringOuter === "number"
      ? ringOuter * RING_INNER_RATIO
      : `${(RING_INNER_RATIO * 100).toFixed(2)}%`);
  const showPadding = filled > 0 && remainder > 0;

  return (
    <div
      className={`flex flex-col items-center w-full ${
        compact ? "" : "h-full justify-center"
      }`}
    >
      <div
        className={cn(
          "relative",
          compact
            ? ""
            : "h-full min-h-[120px] max-h-[240px] aspect-square max-w-full"
        )}
        style={compact ? { width: chartSize, height: chartSize } : undefined}
      >
        <ResponsiveContainer width="100%" height="100%">
          <RechartsPieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={ringInner}
              outerRadius={ringOuter}
              startAngle={90}
              endAngle={-270}
              paddingAngle={showPadding ? 3 : 0}
              dataKey="value"
              cornerRadius={10}
              strokeWidth={0}
              isAnimationActive={false}
            >
              {data.map((entry) => (
                <Cell key={entry.name} fill={entry.color} stroke="none" />
              ))}
            </Pie>
            {showTooltip && (
              <RechartsTooltip
                content={
                  <GaugeTooltip
                    tooltipLabel={tooltipLabel as string}
                    display={centerValue as string}
                  />
                }
              />
            )}
          </RechartsPieChart>
        </ResponsiveContainer>
        {(centerLabel || centerValue) && (
          <div className="absolute inset-0 flex flex-col gap-1 items-center justify-center pointer-events-none">
            {centerValue && (
              <Text
                variant="lgLabel"
                className={compact ? "text-[30px]" : "text-[36px]"}
              >
                {centerValue}
              </Text>
            )}
            {centerLabel && (
              <Text variant={compact ? "smBodySecondary" : "bodySecondary"}>
                {centerLabel}
              </Text>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DreamerGaugeChart;
